// ============================================================
// PlatformNotificationTypesPage.js
// Route /platform/notification-types — 34 platform NotificationTypes.
// Schema has Description (no Title) + DefaultChannels (csv) +
// NanoPromptTemplate (the gpt-5-nano render prompt).
//
// Add is NOT exposed — adding new notification types requires a
// backend code change (the type drives a server-side trigger).
// The brief mentions a create form, but APIM ships only GET and
// PATCH for this resource — keeping the surface honest. Captured
// as a backlog item.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Switch from '../components/Switch';
import Modal from '../components/Modal';
import Field from '../components/Field';
import Badge from '../components/Badge';
import { toast } from '../components/Toaster';
import {
  listPlatformNotificationTypes, patchPlatformNotificationType,
} from '../api/adminPlatform';
import { translateError } from '../utils/errorMessages';
import './PlatformPages.css';

function parseChannels(csv) {
  const set = new Set((csv || '').split(',').map((s) => s.trim()).filter(Boolean));
  return {
    teams:  set.has('teams'),
    email:  set.has('email'),
    in_app: set.has('in_app'),
  };
}
function buildChannelsCsv(c) {
  const out = [];
  if (c.teams)  out.push('teams');
  if (c.email)  out.push('email');
  if (c.in_app) out.push('in_app');
  return out.join(',');
}

const BLANK_FORM = {
  description: '',
  channels: { teams: false, email: true, in_app: true },
  recipientType: 'user',
  deduplicationWindowHours: 0,
  nanoPromptTemplate: '',
};

export default function PlatformNotificationTypesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listPlatformNotificationTypes();
      setItems(res.items || []);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  function openEdit(row) {
    setEditingId(row.notificationTypeId);
    setEditingCode(row.code);
    setForm({
      description: row.description,
      channels: parseChannels(row.defaultChannels),
      recipientType: row.recipientType,
      deduplicationWindowHours: row.deduplicationWindowHours,
      nanoPromptTemplate: row.nanoPromptTemplate,
    });
    setFormError(null);
    setIsFormOpen(true);
  }
  function closeForm() {
    if (submitting) return;
    setIsFormOpen(false); setEditingId(null); setEditingCode(null); setForm(BLANK_FORM);
  }

  async function submitForm(e) {
    e.preventDefault();
    setFormError(null); setSubmitting(true);
    const channelsCsv = buildChannelsCsv(form.channels);
    if (!channelsCsv) {
      setFormError('At least one channel must be enabled.');
      setSubmitting(false);
      return;
    }
    try {
      await patchPlatformNotificationType(editingId, {
        description: form.description,
        defaultChannels: channelsCsv,
        recipientType: form.recipientType,
        deduplicationWindowHours: form.deduplicationWindowHours,
        nanoPromptTemplate: form.nanoPromptTemplate,
      });
      toast.success('Notification type updated');
      setIsFormOpen(false); setEditingId(null); setEditingCode(null); setForm(BLANK_FORM);
      await fetchData();
    } catch (err) { setFormError(translateError(err)); }
    finally { setSubmitting(false); }
  }

  async function toggleActive(row, nextActive) {
    setPendingId(row.notificationTypeId);
    try {
      await patchPlatformNotificationType(row.notificationTypeId, { isActive: nextActive });
      toast.success(nextActive ? 'Notification type activated' : 'Notification type deactivated');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setPendingId(null); }
  }

  const columns = [
    { key: 'code', label: 'Code', render: (r) => <code className="platform-page__code">{r.code}</code>, width: '280px' },
    { key: 'description', label: 'Description', render: (r) => <span style={{ fontSize: 13 }}>{r.description}</span> },
    { key: 'channels', label: 'Default Channels', width: '170px',
      render: (r) => (
        <div style={{ display: 'flex', gap: 4 }}>
          {(r.defaultChannels || '').split(',').filter(Boolean).map((ch) => (
            <Badge key={ch} variant="neutral">{ch}</Badge>
          ))}
        </div>
      ),
    },
    { key: 'dedup', label: 'Dedup', width: '70px', align: 'right',
      render: (r) => `${r.deduplicationWindowHours}h`,
    },
    { key: 'recipient', label: 'Recipient Type', width: '140px', render: (r) => r.recipientType },
    { key: 'active', label: 'Status', width: '90px',
      render: (r) => (
        <Switch
          checked={r.isActive}
          onChange={(next) => toggleActive(r, next)}
          disabled={pendingId === r.notificationTypeId}
          ariaLabel={`${r.code} active`}
        />
      ),
    },
    { key: 'edit', label: '', width: '80px',
      render: (r) => (
        <button type="button" className="platform-page__row-action" onClick={() => openEdit(r)}>
          Edit
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Notification Types"
        subtitle="34 platform notification types. Adding new types requires a backend code change — edit existing types here."
      />

      <DataTable
        data={items}
        columns={columns}
        rowKey={(r) => r.notificationTypeId}
        loading={loading}
        error={error}
        onRetry={fetchData}
        totalCount={items.length}
        page={1}
        pageSize={items.length || 1}
        onPageChange={() => {}}
        emptyMessage="No notification types defined."
      />

      <Modal isOpen={isFormOpen} onClose={closeForm} title={`Edit ${editingCode || ''}`} closeDisabled={submitting}>
        <form onSubmit={submitForm} className="platform-page__form">
          <Field label="Description" required>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              maxLength={4000}
              required
            />
          </Field>
          <Field label="Default Channels" required>
            <div className="platform-page__channel-checkboxes">
              <label><input type="checkbox" checked={form.channels.teams}  onChange={(e) => setForm({ ...form, channels: { ...form.channels, teams: e.target.checked } })} /> Teams</label>
              <label><input type="checkbox" checked={form.channels.email}  onChange={(e) => setForm({ ...form, channels: { ...form.channels, email: e.target.checked } })} /> Email</label>
              <label><input type="checkbox" checked={form.channels.in_app} onChange={(e) => setForm({ ...form, channels: { ...form.channels, in_app: e.target.checked } })} /> In-app</label>
            </div>
          </Field>
          <Field label="Recipient Type" required help="user / role:manager / role:hr / role:manager,role:hr / etc.">
            <input
              type="text"
              value={form.recipientType}
              onChange={(e) => setForm({ ...form, recipientType: e.target.value })}
              maxLength={200}
              required
            />
          </Field>
          <Field label="Deduplication Window (hours)" required help="0-168">
            <input
              type="number"
              min={0} max={168}
              value={form.deduplicationWindowHours}
              onChange={(e) => setForm({ ...form, deduplicationWindowHours: parseInt(e.target.value, 10) || 0 })}
              required
            />
          </Field>
          <Field label="Nano prompt template" required help="The gpt-5-nano render prompt. Curly-brace tokens get substituted at send time.">
            <textarea
              value={form.nanoPromptTemplate}
              onChange={(e) => setForm({ ...form, nanoPromptTemplate: e.target.value })}
              rows={6}
              maxLength={10000}
              required
              style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
            />
          </Field>
          {formError && <p className="platform-page__form-error" role="alert">{formError}</p>}
          <div className="platform-page__form-actions">
            <button type="button" onClick={closeForm} disabled={submitting} className="platform-page__btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="platform-page__btn-primary">
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
